import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { isAuthenticated, getToken, logout } from '../services/authService';
import { User } from '../types';

// Define the shape of our context
interface AuthContextType {
  isLoggedIn: boolean;
  user: User | null;
  logout: () => void;
  setUser: (user: User) => void;
}

// Create the context with default values
const AuthContext = createContext<AuthContextType>({
  isLoggedIn: false,
  user: null,
  logout: () => {},
  setUser: () => {},
});

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Props for the AuthProvider component
interface AuthProviderProps {
  children: ReactNode;
}

// AuthProvider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(isAuthenticated());
  const [user, setUser] = useState<User | null>(null);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = () => {
      const token = getToken();
      if (token) {
        // In a real app, you would decode the JWT token or make an API call
        // to get the user information
        // For now, we'll just set a dummy user
        setIsLoggedIn(true);
        
        // Try to get user from localStorage (in a real app, this would come from the token)
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          setUser(JSON.parse(storedUser));
        } else {
          setUser({
            id: 1,
            email: 'user@example.com',
            first_name: 'Demo',
            last_name: 'User',
            full_name: 'Demo User',
            role: 'student',
            school: 'Demo High School',
            grade: '11th Grade',
            interests: ['Computer Science', 'Robotics'],
            achievements: ['Science Fair Winner', 'Coding Competition Finalist'],
            gmail_connected: false,
            gmail_email: '',
            default_email_provider: 'sendgrid',
            // Aliases for camelCase consistency
            firstName: 'Demo',
            lastName: 'User',
          });
        }
      } else {
        setIsLoggedIn(false);
        setUser(null);
      }
    };

    checkAuth();
  }, []);

  // Handle user logout
  const handleLogout = () => {
    logout();
    setIsLoggedIn(false);
    setUser(null);
  };

  // Handle setting user
  const handleSetUser = (newUser: User) => {
    setUser(newUser);
    setIsLoggedIn(true);
    localStorage.setItem('user', JSON.stringify(newUser));
  };

  // Context value
  const value = {
    isLoggedIn,
    user,
    logout: handleLogout,
    setUser: handleSetUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
